import React, { useState, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import { apiService } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Loader2,
  AlertCircle,
  CheckCircle2,
  Check,
  Sparkles,
  Calendar,
  ArrowRight,
  Receipt,
  XCircle,
} from 'lucide-react'
import PageHeader from '@/components/PageHeader'
import { formatDate } from '@/lib/utils'

// ─── Constants ────────────────────────────────────────────────────────────────

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatAmount = (amount, currency = 'usd') => {
  const value = Number(amount)
  if (Number.isNaN(value)) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
  }).format(value)
}

const parseFeatures = (features) => {
  if (!features) return null
  if (typeof features === 'object') return features
  try { return JSON.parse(features) } catch { return null }
}

const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : str)

// Status chips for the current-plan card
const HERO_STATUS = {
  active:   { label: 'Active',   cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  trialing: { label: 'Trial',    cls: 'bg-accent text-blue-800 border-transparent' },
  past_due: { label: 'Past due', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  canceled: { label: 'Canceled', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  inactive: { label: 'No plan',  cls: 'bg-slate-100 text-slate-600 border-slate-200' },
}

// ─── Feature row ──────────────────────────────────────────────────────────────

function Feature({ label }) {
  return (
    <div className="flex items-center gap-2.5 text-sm text-slate-600">
      <div className="w-4 h-4 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
        <Check className="h-2.5 w-2.5 text-emerald-600" />
      </div>
      {label}
    </div>
  )
}

// ─── Section header ───────────────────────────────────────────────────────────

function SectionHeader({ title, subtitle }) {
  return (
    <div className="mb-5">
      <h2 className="text-[15px] font-semibold text-slate-900">{title}</h2>
      {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
    </div>
  )
}

// ─── Plan card ────────────────────────────────────────────────────────────────

function PlanCard({ plan, isCurrentPlan, onChoose, choosing }) {
  const features = parseFeatures(plan.features)

  return (
    <div
      className={`relative flex flex-col overflow-hidden rounded-[14px] border transition-colors ${
        isCurrentPlan ? 'border-primary bg-accent' : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
    >

      <div className="p-6 flex flex-col flex-1">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0">
            <p className="truncate text-base font-semibold text-slate-900">{plan.name}</p>
            {plan.description && (
              <p className="text-xs text-slate-500 mt-0.5 leading-relaxed line-clamp-2">
                {plan.description}
              </p>
            )}
          </div>
          {isCurrentPlan && (
            <span className="ml-3 flex-shrink-0 rounded-full bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground">
              Current
            </span>
          )}
        </div>

        {/* Price */}
        <div className="flex items-baseline gap-1.5 mb-5">
          <span className="text-3xl font-semibold tracking-[-0.025em] text-slate-900">
            {formatAmount(plan.amount, plan.currency)}
          </span>
          <span className="text-sm text-slate-400 font-medium">/ {plan.interval}</span>
        </div>

        {/* Feature list */}
        {features && (
          <div className="space-y-2.5 flex-1 border-t border-slate-100 pt-4 mb-5">
            {typeof features.clients === 'number' && (
              <Feature label={`Up to ${features.clients} clients`} />
            )}
            {typeof features.consultants === 'number' && (
              <Feature label={`Up to ${features.consultants} consultants`} />
            )}
            {Array.isArray(features.features) &&
              features.features.map((f) => <Feature key={f} label={f} />)}
          </div>
        )}

        {/* CTA */}
        <Button
          onClick={() => onChoose(plan)}
          disabled={isCurrentPlan || choosing}
          className={`mt-auto h-10 w-full gap-2 ${
            isCurrentPlan ? 'cursor-not-allowed bg-slate-100 text-slate-500 hover:bg-slate-100' : ''
          }`}
        >
          {choosing ? (
            <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting…</>
          ) : isCurrentPlan ? (
            'Current Plan'
          ) : (
            <>Choose Plan <ArrowRight className="h-4 w-4" /></>
          )}
        </Button>
      </div>
    </div>
  )
}

// ─── Main ─────────────────────────────────────────────────────────────────────

const Billing = () => {
  const { companyUrl } = useAuthStore()

  const [subscription, setSubscription] = useState(null)
  const [loadingSubscription, setLoadingSubscription] = useState(true)
  const [subscriptionError, setSubscriptionError] = useState(null)

  const [history, setHistory] = useState([])
  const [loadingHistory, setLoadingHistory] = useState(true)
  const [historyError, setHistoryError] = useState(null)

  const [plans, setPlans] = useState([])
  const [loadingPlans, setLoadingPlans] = useState(true)
  const [plansError, setPlansError] = useState(null)
  const [checkoutPlanId, setCheckoutPlanId] = useState(null)
  const [checkoutError, setCheckoutError] = useState(null)

  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [cancelSaving, setCancelSaving] = useState(false)
  const [cancelError, setCancelError] = useState(null)
  const [cancelSuccess, setCancelSuccess] = useState(false)

  const loadSubscription = useCallback(async () => {
    setLoadingSubscription(true)
    setSubscriptionError(null)
    const result = await apiService.fetchCurrentSubscription(companyUrl)
    if (result.success) {
      setSubscription(result.data)
    } else {
      setSubscriptionError(result.error || 'Failed to load current subscription.')
    }
    setLoadingSubscription(false)
  }, [companyUrl])

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true)
    setHistoryError(null)
    const result = await apiService.fetchBillingHistory(companyUrl)
    if (result.success) {
      setHistory(Array.isArray(result.data?.history) ? result.data.history : [])
    } else {
      setHistoryError(result.error || 'Failed to load billing history.')
    }
    setLoadingHistory(false)
  }, [companyUrl])

  const loadPlans = useCallback(async () => {
    setLoadingPlans(true)
    setPlansError(null)
    const result = await apiService.fetchBillingPlans(companyUrl)
    if (result.success) {
      setPlans(Array.isArray(result.data) ? result.data : [])
    } else {
      setPlansError(result.error || 'Failed to load subscription plans.')
    }
    setLoadingPlans(false)
  }, [companyUrl])

  useEffect(() => {
    if (!companyUrl) {
      setLoadingSubscription(false)
      setLoadingHistory(false)
      setLoadingPlans(false)
      return
    }
    loadSubscription()
    loadHistory()
    loadPlans()
  }, [companyUrl, loadSubscription, loadHistory, loadPlans])

  const handleChoosePlan = async (plan) => {
    setCheckoutPlanId(plan.id)
    setCheckoutError(null)

    const result = await apiService.createBillingCheckoutSession(companyUrl, {
      priceId: plan.stripe_price_id,
      success_url: `${window.location.origin}/billing/success`,
      cancel_url: `${window.location.origin}/billing/cancel`,
    })

    if (!result.success) {
      setCheckoutError(result.error || 'Failed to start checkout.')
      setCheckoutPlanId(null)
      return
    }

    if (result.data?.url) {
      window.location.href = result.data.url
    } else {
      setCheckoutError('Checkout session created but no redirect URL was returned.')
      setCheckoutPlanId(null)
    }
  }

  const handleOpenCancelDialog = () => {
    setCancelError(null)
    setShowCancelDialog(true)
  }

  const handleConfirmCancel = async () => {
    setCancelSaving(true)
    setCancelError(null)

    const result = await apiService.cancelBillingSubscription(companyUrl, {
      cancel_at_period_end: true,
    })

    if (!result.success) {
      setCancelError(result.error || 'Failed to cancel subscription.')
      setCancelSaving(false)
      return
    }

    setCancelSaving(false)
    setShowCancelDialog(false)
    setCancelSuccess(true)
    setTimeout(() => setCancelSuccess(false), 4000)
    await loadSubscription()
  }

  const hasActiveSubscription = subscription?.status && subscription.status !== 'inactive'
  const heroStatus = HERO_STATUS[subscription?.status] || HERO_STATUS.inactive

  return (
    <div className="space-y-6">
      <PageHeader
        title="Billing & Plans"
        subtitle="Manage your subscription, payment method, and billing history"
      />

      {/* ── Cancel success banner ── */}
      {cancelSuccess && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2.5 bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-3.5 text-sm text-emerald-700"
        >
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
          Your subscription will be canceled at the end of the current billing period.
        </motion.div>
      )}

      {/* ── Current plan ── */}
      <div className="rounded-[14px] border border-slate-200 bg-white px-5 py-4">
        {loadingSubscription ? (
          <div className="flex items-center gap-2.5 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading subscription…</span>
          </div>
        ) : subscriptionError ? (
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm">{subscriptionError}</span>
          </div>
        ) : !hasActiveSubscription ? (
          <div className="flex flex-wrap items-center gap-3">
            <span className={`flex-shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${HERO_STATUS.inactive.cls}`}>
              {HERO_STATUS.inactive.label}
            </span>
            <span className="font-semibold text-slate-900">No active subscription</span>
            <span className="text-sm text-slate-500">· Select a plan below to get started</span>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <span className={`flex-shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${heroStatus.cls}`}>
                {heroStatus.label}
              </span>
              <span className="truncate text-base font-semibold text-slate-900">
                {subscription.plan?.name || 'Subscription'}
              </span>
              {subscription.plan && (
                <span className="text-sm text-slate-600">
                  {formatAmount(subscription.plan.amount, subscription.plan.currency)}
                  <span className="ml-1 text-slate-500">/ {subscription.plan.interval}</span>
                </span>
              )}
              {subscription.current_period_end && (
                <span className="flex flex-shrink-0 items-center gap-1 text-xs text-slate-500">
                  <Calendar className="h-3 w-3" />
                  {subscription.cancel_at_period_end ? 'Cancels' : 'Renews'} {formatDate(subscription.current_period_end)}
                </span>
              )}
              {subscription.cancel_at_period_end && (
                <span className="flex-shrink-0 text-xs font-semibold text-amber-700">· Cancels at period end</span>
              )}
            </div>

            {!subscription.cancel_at_period_end && (
              <Button variant="outline" size="sm" onClick={handleOpenCancelDialog} className="flex-shrink-0 gap-1.5">
                <XCircle className="h-3.5 w-3.5" />
                Cancel plan
              </Button>
            )}
          </div>
        )}
      </div>

      {/* ── Available Plans ── */}
      <div className="app-panel p-6">
        <SectionHeader
          title="Available plans"
          subtitle="Upgrade or change your plan — you'll be taken to a secure Stripe checkout"
        />

        {checkoutError && (
          <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 mb-4">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{checkoutError}</span>
          </div>
        )}

        {loadingPlans ? (
          <div className="flex items-center justify-center gap-2 text-slate-500 py-12">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading plans…</span>
          </div>
        ) : plansError ? (
          <div className="flex items-center gap-2 text-red-500 py-4">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">{plansError}</span>
          </div>
        ) : plans.length === 0 ? (
          <div className="text-center py-10">
            <Sparkles className="h-8 w-8 mx-auto mb-3 text-slate-300" />
            <p className="text-sm text-slate-400">No plans available right now.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                isCurrentPlan={subscription?.plan?.id === plan.id}
                choosing={checkoutPlanId === plan.id}
                onChoose={handleChoosePlan}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Payment History ── */}
      <div className="app-panel p-6">
        <SectionHeader
          title="Payment History"
          subtitle="All invoices and receipts for your company"
        />

        {loadingHistory ? (
          <div className="flex items-center justify-center gap-2 text-slate-500 py-12">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading history…</span>
          </div>
        ) : historyError ? (
          <div className="flex items-center gap-2 text-red-500 py-4">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">{historyError}</span>
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-10">
            <Receipt className="h-8 w-8 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500 font-medium">No payments yet</p>
            <p className="text-xs text-slate-400 mt-1">
              Your invoices will appear here once a plan is active.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-slate-100">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/70">
                  <th className="text-left px-5 py-3 app-kicker">Date</th>
                  <th className="text-left px-5 py-3 app-kicker">Description</th>
                  <th className="text-right px-5 py-3 app-kicker">Amount</th>
                  <th className="text-left px-5 py-3 app-kicker">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {history.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/70 transition-colors">
                    <td className="px-5 py-3.5 text-slate-500 whitespace-nowrap text-[13px]">
                      {formatDate(item.created_at)}
                    </td>
                    <td className="px-5 py-3.5 text-slate-700 text-[13px]">
                      {item.description || '—'}
                    </td>
                    <td className="px-5 py-3.5 text-right font-semibold text-slate-900 tabular-nums text-[13px]">
                      {formatAmount(item.amount, item.currency)}
                    </td>
                    <td className="px-5 py-3.5">
                      <span
                        className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${
                          item.status === 'paid'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : item.status === 'failed'
                            ? 'bg-red-50 text-red-600 border-red-200'
                            : 'bg-slate-100 text-slate-600 border-slate-200'
                        }`}
                      >
                        {capitalize(item.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Cancel confirmation dialog ── */}
      <Dialog
        open={showCancelDialog}
        onOpenChange={(open) => {
          if (!cancelSaving) setShowCancelDialog(open)
        }}
      >
        <DialogContent className="max-w-md rounded-[28px]">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2.5 bg-red-100 rounded-2xl">
                <XCircle className="h-5 w-5 text-red-500" />
              </div>
              <DialogTitle className="text-lg font-semibold text-slate-800">
                Cancel Subscription
              </DialogTitle>
            </div>
            <DialogDescription className="text-sm text-slate-500 leading-relaxed pt-1">
              Your plan stays active until{' '}
              {subscription?.current_period_end
                ? formatDate(subscription.current_period_end)
                : 'the end of the billing period'}
              , then it won't renew. No charges after that.
            </DialogDescription>
          </DialogHeader>

          {cancelError && (
            <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{cancelError}</span>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2 pt-2">
            <Button
              variant="outline"
              className="rounded-xl flex-1 sm:flex-none"
              onClick={() => setShowCancelDialog(false)}
              disabled={cancelSaving}
            >
              Keep Plan
            </Button>
            <Button
              onClick={handleConfirmCancel}
              disabled={cancelSaving}
              className="rounded-xl flex-1 sm:flex-none bg-red-500 hover:bg-red-600 text-white gap-1.5"
            >
              {cancelSaving ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Canceling…</>
              ) : (
                <><XCircle className="h-4 w-4" /> Cancel subscription</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default Billing
